import { Box, Button, IconButton, Snackbar, TextField, Typography } from '@mui/material';
import { PollQuestion, PollTemplate, RangePollQuestion, SelectPollQuestion } from '../../types';
import React, { useEffect, useState } from 'react';
import { auth, db } from '../../firebase';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { useNavigate, useParams } from 'react-router-dom';

import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import QuestionForm from './QuestionForm';
import { v4 as uuidv4 } from 'uuid';

const PollForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  const [error, setError] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [showQuestionForm, setShowQuestionForm] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState<PollQuestion | null>(null);
  const [pollTemplate, setPollTemplate] = useState<PollTemplate>({
    id: '',
    title: '',
    description: '',
    questions: [],
    updatedAt: new Date(),
    updatedBy: ''
  });

  useEffect(() => {
    const fetchPoll = async () => {
      if (id) {
        try {
          const pollRef = doc(db, 'polls', id);
          const pollSnap = await getDoc(pollRef);
          
          if (pollSnap.exists()) {
            const template = pollSnap.data() as PollTemplate;
            setPollTemplate(template);
          } else {
            setError('Poll not found');
          }
        } catch (error) {
          console.error('Error fetching poll: ', error);
          setError('Error fetching poll: ' + (error instanceof Error ? error.message : 'Unknown error'));
        }
      }
    };

    fetchPoll();
  }, [id]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!auth.currentUser) {
      setError('You must be logged in to create or edit a poll.');
      return;
    }

    try {
      if (id) {
        // Update existing poll
        const pollRef = doc(db, 'polls', id);
        await updateDoc(pollRef, {
          ...pollTemplate,
          updatedAt: new Date(),
          updatedBy: auth.currentUser.uid
        });
      } else {
        const newPollId = uuidv4();
        const pollRef = doc(db, 'polls', newPollId);
        // Create new poll
        await setDoc(pollRef, {
          ...pollTemplate,
          id: newPollId,
          archived: false,
          createdAt: new Date(),
          createdBy: auth.currentUser.uid
        });
        navigate(`/polls/${newPollId}`);
      }
      setSnackbarMessage('Poll saved successfully');
      setSnackbarOpen(true);
      navigate('/polls');
    } catch (error) {
      console.error('Error saving poll: ', error);
      setError('Error saving poll: ' + (error instanceof Error ? error.message : 'Unknown error'));
      setSnackbarMessage('Failed to save poll. Please try again.');
      setSnackbarOpen(true);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleQuestionSubmit = (submittedQuestion: PollQuestion) => {
    if (editingQuestion) {
      // Update existing question
      setPollTemplate(prevState => ({
        ...prevState,
        questions: prevState.questions.map(q => 
          q.id === submittedQuestion.id ? submittedQuestion : q
        )
      }));
      setEditingQuestion(null);
    } else {
      // Add new question
      setPollTemplate(prevState => ({
        ...prevState,
        questions: [...prevState.questions, submittedQuestion]
      }));
    }
    setShowQuestionForm(false);
  };

  const handleQuestionEditCancel = () => {
    setEditingQuestion(null);
    setShowQuestionForm(false);
  };

  const moveQuestion = (index: number, direction: 'up' | 'down') => {
    const newQuestions = [...pollTemplate.questions];
    if (direction === 'up' && index > 0) {
      [newQuestions[index - 1], newQuestions[index]] = [newQuestions[index], newQuestions[index - 1]];
    } else if (direction === 'down' && index < newQuestions.length - 1) {
      [newQuestions[index], newQuestions[index + 1]] = [newQuestions[index + 1], newQuestions[index]];
    }
    setPollTemplate(prevState => ({
      ...prevState,
      questions: newQuestions
    }));
  };

  const renderQuestionDetails = (question: PollQuestion) => {
    switch (question.type) {
      case 'select':
      case 'multiselect':
        return (
          <> 
            <Typography variant="body2">Options: {(question as SelectPollQuestion).options.join(', ')}</Typography>
            <Typography variant="body2">Control Type: {(question as SelectPollQuestion).controlType}</Typography>
          </>
        );
      case 'range':
        return (
          <> 
            <Typography variant="body2">Min: {(question as RangePollQuestion).min}</Typography>
            <Typography variant="body2">Max: {(question as RangePollQuestion).max}</Typography>
          </>
        );
      case 'text':
        return null;
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 600, margin: 'auto' }}>
      <Typography variant="h4">{id ? 'Edit Poll' : 'Create Poll'}</Typography>
      {error && <Typography color="error">{error}</Typography>}
      
      <TextField
        label="Poll Title"
        value={pollTemplate.title}
        onChange={(e) => setPollTemplate({ ...pollTemplate, title: e.target.value })}
        required
      />
      <TextField
        label="Description"
        value={pollTemplate.description}
        onChange={(e) => setPollTemplate({ ...pollTemplate, description: e.target.value })}
        multiline
        rows={4}
      />

      {pollTemplate.questions.map((question, index) => (
        <Box key={question.id} sx={{ display: 'flex', flexDirection: 'column', gap: 1, border: '1px solid #ccc', padding: 2, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">{index + 1}. {question.text}</Typography>
            <Box>
              <IconButton onClick={() => moveQuestion(index, 'up')} disabled={index === 0}>
                <ArrowUpwardIcon />
              </IconButton>
              <IconButton onClick={() => moveQuestion(index, 'down')} disabled={index === pollTemplate.questions.length - 1}>
                <ArrowDownwardIcon />
              </IconButton>
              <IconButton onClick={() => {
                if (!showQuestionForm) {
                  setEditingQuestion(question);
                  setShowQuestionForm(true);
                }
              }}>
                <EditIcon />
              </IconButton>
              <IconButton onClick={() => {
                setPollTemplate(prevState => ({
                  ...prevState,
                  questions: prevState.questions.filter(q => q.id !== question.id)
                }));
              }}>
                <DeleteIcon />
              </IconButton>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {showQuestionForm && editingQuestion?.id === question.id && (
            <QuestionForm
              onSubmitQuestion={handleQuestionSubmit}
              onCancel={handleQuestionEditCancel}
              editingQuestion={editingQuestion ?? undefined}
            />
          )}
          </Box>
          <Typography variant="body2">Type: {question.type}</Typography>
          {renderQuestionDetails(question)}
        </Box>
      ))}

      {showQuestionForm && !editingQuestion ? (
        <QuestionForm
          onSubmitQuestion={handleQuestionSubmit}
          onCancel={handleQuestionEditCancel}
          editingQuestion={undefined}
        />
      ) : (
        <Button
          type="button"
          variant="contained"
          onClick={() => {
            setEditingQuestion(null);
            setShowQuestionForm(true);
          }}
        >
          New Question
        </Button>
      )}

      <Button type="submit" variant="contained">
        {id ? 'Update Poll' : 'Create Poll'}
      </Button>

      <Snackbar
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
      />
    </Box>
  );
};

export default PollForm;
